// © Microsoft Corporation. All rights reserved.

import { ConfigOrigin, ResolverEnvironment } from "@msnews/core-contracts";
import { ConfigReference, ExperienceConfig } from "@msnews/config-data-types";
import { enableIdbCaching, getCacheKeyFromConfigRef, isConfigBundleLocHashSameAsLastPage } from "../../utilities";

import { ClientResolver } from "./client-fallback-resolver/ClientResolver";
import { ConfigCache } from "./ConfigCache";
import { ConfigService } from "../ConfigService";
import { getRootConfigRef } from "@msnews/web-worker-shared-utils";

/**
 * Git config resolver class
 */
export class ConfigResolver {
    /**
     * Config service object
     */
    private readonly configService: ConfigService;

    /**
     * Config cache object
     */
    private readonly configCache: ConfigCache;

    /**
     * ClientResolver object
     */
    private readonly clientResolver: ClientResolver;

    /**
     * Set to true when fallback client side resolver is called
     */
    private fallbackResolverCalled = false;

    /**
     * Flag to indicate if the initial CRS bulk fetch call has finished fetching.
     * This flag is used to avoid extra awaiting on completed Promises for new getConfig calls.
     */
    private crsInitComplete = false;

    /**
     * True if bundle, config and loc hash of current page is same as last page
     */
    private readonly configsEqual: boolean;

    /**
     * If this is treatment, use IndexedDB cache else do not.
     */
    private enableIdbCaching = false;

    /**
     * Config resolver constructor to initialize child classes.
     * @param {ResolverEnvironment} resolverEnvironment
     */
    constructor(readonly resolverEnvironment: ResolverEnvironment) {
        if (!resolverEnvironment || !resolverEnvironment.targetScope) {
            throw new Error("ResolverEnvironment or targetScope does not exist.");
        }

        // Initialize configService object
        this.configService = new ConfigService(resolverEnvironment);

        // True if bundle, config and loc hash of current page is same as last page
        this.configsEqual = isConfigBundleLocHashSameAsLastPage(resolverEnvironment.configCacheProperties);

        // True if this belong to idb caching of Git configs
        this.enableIdbCaching = enableIdbCaching(resolverEnvironment.targetScope.pageExperiments);

        // Initialize configCache object
        this.configCache = new ConfigCache(resolverEnvironment, this.enableIdbCaching);

        // Initialize fallback resolver
        this.clientResolver = new ClientResolver(resolverEnvironment);
    }

    /**
     * True if CRS fetch had network errors, else false
     */
    public get crsHadNetworkError(): boolean {
        return this.configService.hadNetworkError;
    }

    /**
     * Fast helper to try to get it directly from cache so callers do not have to go through
     * additional async layers to get to the result if we've already loaded up the memory map.
     * @param configRef - The config ref to get from memory cache.
     */
    public tryGetConfigSync(configRef: ConfigReference): ExperienceConfig | undefined {

        if (configRef) {

            // try to do a fast synchronous get from the in-memory caches from each of the layers we have
            return this.configService.tryGetConfigSync(configRef)
                || this.configCache.tryGetConfigSync(configRef);
        }
    }

    /**
     * Returns the resolved merged Experience config from CRS.
     * @param {ConfigReference} configRef: The identity structure that uniquely identifies a single instance of an experience
     * @returns {Promise<ExperienceConfig>} ExperienceConfig
     */
    public async getConfig(configRef: ConfigReference): Promise<ExperienceConfig> {
        if (this.resolverEnvironment.configOrigins.feature === ConfigOrigin.Local
            || this.resolverEnvironment.mockManager.mocksEnabled) {
            const isLoggerCallbackAvailable = typeof this.resolverEnvironment.loggerCallback === "function";
            try {
                const localConfigs = await this.clientResolver.getConfig(configRef);
                if (localConfigs) {
                    return Promise.resolve(localConfigs);
                } else {
                    isLoggerCallbackAvailable && this.resolverEnvironment.loggerCallback(`ConfigResolver: Failed to fetch configs from local configs. Config: ${getCacheKeyFromConfigRef(configRef)}`);
                    return Promise.resolve(null);
                }
            } catch (error) {
                isLoggerCallbackAvailable && this.resolverEnvironment.loggerCallback(`${error.message}`);
                return Promise.reject(error);
            }
        }

        if (!this.crsInitComplete) {
            await this.initiateConfigService();
            this.crsInitComplete = true;
        }

        // try to do a fast synchronous get from the in-memory caches from each of the layers we have
        const fromMemoryConfig = this.tryGetConfigSync(configRef);
        if (fromMemoryConfig) {
            return fromMemoryConfig;
        }

        const networkReadPromise = new Promise<ExperienceConfig>(async (resolve) => {
            const crsConfig = await this.configService.getConfig(configRef);
            if (crsConfig) {
                return resolve(crsConfig);
            }

            // Try fallback client resolver only when CRS returns non-200 and non-404 response.
            if (this.configService.shouldTryFallbackResolver) {
                this.fallbackResolverCalled = true;
                const cdnConfig = await this.clientResolver.getConfig(configRef);
                if (cdnConfig) {
                    return resolve(cdnConfig);
                }
            }

            resolve(null);
        });

        const cacheReadPromise = new Promise<ExperienceConfig>(async (resolve) => {
            // Fetch configs from cache only if configs are equal
            if (this.configsEqual && this.enableIdbCaching) {
                const cachedConfig = await this.configCache.getConfig(configRef);
                if (cachedConfig) {
                    return resolve(cachedConfig);
                }
            }

            // If config hashes are not same, do not resolve cacheReadPromise promise!
            // Just so that the networkReadPromise wins when we race between networkReadPromise and cacheReadPromise
        });

        // start the race between network and IDB cache read
        return Promise.race([cacheReadPromise, networkReadPromise]);
    }

    /**
     * This overwrite (update) / write configs into cache
     * This is typically called after TTVR in web worker context
     * @returns true if configs are equal
     */
    public async updateConfigsCache(): Promise<boolean> {
        // We store CMS configs in IndexedDB at client since caching header for CRS call is 15 minutes for DRI scenarios.
        // Git configs have 7 days of caching header, so we want to skip IndexedDB read/write/udpate layer of config caching.
        // At this point, client is set to make a network call and will find network response in the disk cache.
        // If there is a config update, it has new build version number that gets passed to the client and
        // client uses this build version to query CRS and thus that breaks disk cache automatically.
        if (!this.enableIdbCaching) {
            return this.configsEqual;
        }

        // If configs from this page and last page are same, we want to skip writing to cache
        // When configs are equal, we return true. This should not update cache as cache update is not required.
        // If configs are not equal, we return false. This should update cache with new configs
        if (this.configsEqual) {
            return this.configsEqual;
        }

        // Make sure that CRS getConfig is called.
        // So that configMap will be populated in order to write to Cache.
        // TODO: Make sure that this download full config tree, especially maxDepth scenario.
        await this.initiateConfigService();

        // Get configMap downloaded from CRS.
        const crsConfigMap = this.configService.getConfigMap();
        if (crsConfigMap) {
            // Write this CRS downloaded map to Cache.
            this.configCache.updateCache(crsConfigMap);
            return this.configsEqual;
        }

        // Try fallback client resolver only when CRS returns non-200 and non-404 response.
        if (!this.configService.shouldTryFallbackResolver) {
            return this.configsEqual;
        }

        // Make sure that fallback resolver getConfig is called.
        // So that configMap will be populated in order to write to Cache.
        if (!this.fallbackResolverCalled) {
            await this.clientResolver.getConfig(getRootConfigRef());
        }

        // Get configMap downloaded from fallback resolver.
        const clientResolverConfigMap = this.clientResolver.getConfigMap();
        if (clientResolverConfigMap) {
            // Write this network downloaded map to Cache.
            this.configCache.updateCache(clientResolverConfigMap);
        }

        return this.configsEqual;
    }

    /**
     * To be used by the unit tests
     */
    public getConfigServiceObject(): ConfigService {
        return this.configService;
    }

    /**
     * Initialize config service call
     */
    private async initiateConfigService(): Promise<void> {
        await this.configService.initialize();
        await this.configService.getConfig(getRootConfigRef());
    }
}